import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowRight, LogIn, UserPlus, FileSignature, ClipboardCheck, ReceiptText, CreditCard, BarChart3,
  ShieldCheck, Lock, History, HeartHandshake, CheckCircle2, BookOpen,
} from 'lucide-react';
import SEO from '../../components/SEO';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';
import { CareDemoForm } from './CareDemoForm';
import { CarePricing, CareCustomers } from './CarePromo';

const HERO_IMG = '/og/staymii-care.jpg';

const FEATURES = [
  { icon: UserPlus, t: 'Resident registration', d: 'Automatic Resident ID, room assignment, representative details and an approval step before a resident goes live.' },
  { icon: FileSignature, t: 'Digital admission forms', d: 'Admission Form, PDPA Notice & Consent (English + BM) and Resident Contract, with on-screen signatures and printable PDFs.' },
  { icon: ClipboardCheck, t: 'Admission checklist', d: 'Track documents and belongings. Admission is only marked complete when every form and required document is in.' },
  { icon: ReceiptText, t: 'Monthly billing', d: 'Accommodation, care, recurring and one-time charges, deposits and discounts. Invoices are created automatically every month.' },
  { icon: CreditCard, t: 'Pay Now links', d: 'Families pay online by card. Payments are verified with the gateway before an invoice is marked paid. Offline payments can be recorded too.' },
  { icon: BarChart3, t: 'Receipts & reports', d: 'Professional receipts by PDF or email, plus resident, admission, invoice, payment, outstanding and overdue reports with CSV export.' },
];

const STEPS = ['Register the resident', 'Complete & sign the 3 forms', 'Upload supporting documents', 'Set monthly fees', 'Send the invoice with Pay Now', 'Receipt issued automatically'];

export default function StayMiiCare() {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return undefined;
    const t = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth' }), 300);
    return () => clearTimeout(t);
  }, [hash]);
  return (
    <div className="min-h-screen bg-[#F7F4EE]" data-testid="staymii-care-page">
      <SEO
        title="StayMii Care — Nursing Home Management System Malaysia | Resident Records & Billing"
        description="StayMii Care is a simple nursing home management system for Malaysia: resident registration, digital admission forms with e-signatures, PDPA consent, monthly billing, online Pay Now links and receipts."
        keywords="nursing home management system malaysia, elderly care home software, resident billing system, care home admission form, pusat jagaan warga emas sistem"
        canonicalUrl="/staymii-care"
        ogImage="https://staymii.com/og/staymii-care.jpg"
      />
      <LandingNavBar />

      <section className="pt-28 pb-16 lg:pt-32 lg:pb-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-wide text-teal-800 bg-teal-100/70 rounded-full px-3 py-1">
              <HeartHandshake className="w-3.5 h-3.5" /> StayMii Care™ · Nursing home resident & billing system
            </span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold text-[#181B30] leading-[1.05] tracking-tight" data-testid="care-hero-title">
              Admissions, records and billing for your care home — in one place.
            </h1>
            <p className="mt-6 text-base md:text-lg text-slate-600 max-w-xl">
              Register residents, complete signed admission documents, send monthly invoices with a Pay Now link and issue receipts automatically. Built for Malaysian nursing homes.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a href="#demo" data-testid="care-request-demo" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-teal-700 hover:bg-teal-800 text-white font-semibold transition-colors">
                Request a demo <ArrowRight className="w-4 h-4" />
              </a>
              <Link to="/care/login" data-testid="care-sign-in" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-white border border-slate-300 hover:border-[#181B30] text-[#181B30] font-semibold transition-colors">
                <LogIn className="w-4 h-4" /> Sign in to StayMii Care
              </Link>
            </div>
            <Link to="/staymii-care/guide" data-testid="care-guide-link" className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-teal-800 hover:text-teal-950 underline underline-offset-4 decoration-teal-300 hover:decoration-teal-700 transition-colors">
              <BookOpen className="w-4 h-4" /> Families: how to fill the registration forms
            </Link>
          </div>
          <div className="lg:col-span-6 relative">
            <img src={HERO_IMG} alt="StayMii Care — Care Management, Made Simple: resident registration, admission forms and billing in one place" className="w-full rounded-[28px] shadow-xl" />
          </div>
        </div>
      </section>

      <section className="pb-16 lg:pb-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-2 gap-10 items-center">
          <CareCustomers />
          <CarePricing />
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <h2 className="text-base md:text-lg font-semibold text-teal-800">What StayMii Care does</h2>
          <p className="mt-2 text-3xl sm:text-4xl font-bold text-[#181B30] max-w-2xl">Only what a care home needs. Nothing it doesn't.</p>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
            {FEATURES.map((f, i) => (
              <div key={f.t} data-testid={`care-feature-${i}`}>
                <div className="w-11 h-11 rounded-xl bg-[#181B30] text-teal-300 flex items-center justify-center"><f.icon className="w-5 h-5" /></div>
                <h3 className="mt-4 font-semibold text-slate-900">{f.t}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-2 gap-12">
          <div>
            <h2 className="text-base md:text-lg font-semibold text-teal-800">How it works</h2>
            <ol className="mt-6 space-y-4">
              {STEPS.map((s, i) => (
                <li key={s} className="flex items-center gap-4">
                  <span className="w-9 h-9 rounded-full border-2 border-[#181B30] flex items-center justify-center text-sm font-bold text-[#181B30] shrink-0">{i + 1}</span>
                  <span className="text-slate-800 font-medium">{s}</span>
                </li>
              ))}
            </ol>
          </div>
          <div className="bg-[#181B30] rounded-[28px] p-8 lg:p-10 text-white">
            <h2 className="text-base md:text-lg font-semibold text-teal-300">Built to protect resident data</h2>
            <ul className="mt-6 space-y-5 text-sm text-slate-300">
              <li className="flex gap-3"><Lock className="w-5 h-5 text-teal-300 shrink-0" />Secure company login with Admin, Staff and Finance roles — finance users never see medical information.</li>
              <li className="flex gap-3"><ShieldCheck className="w-5 h-5 text-teal-300 shrink-0" />Documents can only be viewed or downloaded by authorised users. PDPA consent captured digitally.</li>
              <li className="flex gap-3"><History className="w-5 h-5 text-teal-300 shrink-0" />Full audit trail. Issued receipts are never deleted — corrections are voided and traceable.</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="pb-20 scroll-mt-24" id="demo">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <h2 className="text-base md:text-lg font-semibold text-teal-800">Request a demo</h2>
            <p className="mt-2 text-3xl font-bold text-[#181B30]">See StayMii Care with your own forms.</p>
            <p className="mt-4 text-slate-600">Tell us about your care home and we'll set up a walkthrough.</p>
            <div className="mt-8 space-y-3 text-sm">
              <Link to="/care/register" data-testid="care-register-link" className="flex items-center gap-2 text-teal-800 font-semibold hover:underline"><UserPlus className="w-4 h-4" />Families: register a resident online</Link>
              <Link to="/care/login" data-testid="care-cta-sign-in" className="flex items-center gap-2 text-[#181B30] font-semibold hover:underline"><LogIn className="w-4 h-4" />Staff sign in</Link>
            </div>
          </div>
          <div className="lg:col-span-7"><CareDemoForm /></div>
        </div>
      </section>
      <LandingFooter />
    </div>
  );
}
